import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from '../firebase';
import { ref, get } from "firebase/database";
import './Auth.css';
import { useAuth } from '../AuthContext';
import AuthNavbar from './AuthNavbar';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();

  const isFoodLogin = !location.pathname.startsWith('/ride-login');

  const getRedirectPath = () => {
    if (!location.state?.from) {
      return isFoodLogin ? '/food-vendors' : '/';
    }
    if (typeof location.state.from === 'string') {
      return location.state.from;
    }
    const pathname = location.state.from.pathname || '/';
    const search = location.state.from.search || '';
    return `${pathname}${search}`;
  };

  const config = {
    title: isFoodLogin ? "Log in for Food Delivery" : "Log in to Your Account",
    subtitle: isFoodLogin ? "Get your favorite meals delivered." : "Welcome back! Please enter your details.",
    from: getRedirectPath(),
    signupPath: isFoodLogin ? '/signup' : '/ride-signup',
    otherLogin: isFoodLogin
      ? { path: '/ride-login', text: 'Looking for a ride?' }
      : { path: '/login', text: 'Hungry? Order food' }
  };
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const riderRef = ref(db, 'riders/' + user.uid);
      const snapshot = await get(riderRef);

      if (snapshot.exists()) {
        console.log('Logged in user:', user);
        navigate(config.from, { replace: true });
      } else {
        // This user exists in Firebase Auth, but not as a rider in the database.
        // It could be a driver. We should sign them out and give a specific error.
        const driverRef = ref(db, 'drivers/' + user.uid);
        const driverSnapshot = await get(driverRef);
        await signOut();
        if (driverSnapshot.exists()) {
          setError("This appears to be a driver account. Please use the driver login.");
        } else {
          setError("No user account found for this email. Please sign up.");
        }
        console.error("Login failed: User is not a registered rider.");
      }
    } catch (error) {
      setError(error.message.replace('Firebase: ', ''));
      console.error("Error logging in:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      <AuthNavbar />
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-header">
            <h1>{config.title}</h1>
            <p>{config.subtitle}</p>
          </div>

            <form onSubmit={handleLogin} className="auth-form">
              {error && <p className="auth-error">{error}</p>}
              <div className="auth-input-group">
                <label htmlFor="email">Email Address</label>
                <div className="auth-input-wrapper">
                  <input
                    type="email"
                    id="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <div className="auth-input-icons">
                    {email && (
                      <button type="button" className="auth-input-icon-btn" onClick={() => setEmail('')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="auth-input-group">
                <label htmlFor="password">Password</label>
                <div className="auth-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <div className="auth-input-icons">
                    {password && (
                      <button type="button" className="auth-input-icon-btn" onClick={() => setPassword('')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    )}
                    <button type="button" className="auth-input-icon-btn" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              <div className="auth-form-footer">
                <Link to="/forgot-password">Forgot password?</Link>
              </div>
              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? 'Logging In...' : 'Log In'}
              </button>
            </form>

           <div className="auth-redirect">
              <p>Don't have an account? <Link to={config.signupPath} state={{ from: location.state?.from }}>Sign up</Link></p>
              <p style={{ marginTop: '12px' }}><Link to={config.otherLogin.path} state={{ from: location.state?.from }}>{config.otherLogin.text}</Link></p>
           </div>
          </div>
        </div>
        <div className="auth-footer-art"></div>
      </div>
  );
}

export default Login;